import type { CustomerLegalState, CustomerLegalStatusResponse } from './customer-legal-shared';

const CUSTOMER_LEGAL_FETCH_RETRY_DELAYS_MS = [300, 1200, 2500] as const;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchCustomerLegalStatus(): Promise<CustomerLegalState> {
  const response = await fetch('/api/customer-legal', {
    cache: 'no-store',
    credentials: 'same-origin',
  });
  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json')
    ? ((await response.json().catch(() => ({}))) as
        | CustomerLegalStatusResponse
        | { error?: string })
    : ({ error: 'Unable to load customer agreement status.' } as { error: string });

  if (!response.ok || !('state' in payload)) {
    throw new Error(
      payload && 'error' in payload
        ? payload.error
        : 'Unable to load customer agreement status.',
    );
  }

  return payload.state;
}

export async function fetchCustomerLegalStatusWithRetry(): Promise<CustomerLegalState> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= CUSTOMER_LEGAL_FETCH_RETRY_DELAYS_MS.length; attempt += 1) {
    if (attempt > 0) {
      await wait(CUSTOMER_LEGAL_FETCH_RETRY_DELAYS_MS[attempt - 1] ?? 0);
    }

    try {
      return await fetchCustomerLegalStatus();
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error('Unable to load customer agreement status.');
}
